import React, { useState, useEffect, useContext } from "react";
import style from "./RemarketingStyle";
import { AuthContext } from "../../Context/AuthContext";
import { ChatContext } from "../../Context/ChatContext";
import { LoadingContext } from "../../Context/LoadingContext";
import { SystemMessageContext } from "../../Context/SystemMessageContext";
import {
  obterConfiguracaoRemarketing,
  salvarConfiguracaoRemarketing,
  obterModeloRemarketing,
} from "../../Services/dbservice";
import {
  FiSave,
  FiInfo,
  FiX,
  FiAlertTriangle,
  FiSearch,
  FiCheckSquare,
  FiSend,
  FiMessageSquare,
} from "react-icons/fi";
import "./RemarketingAnimations.css";

const InfoModal = ({ onClose, isOpen }) => (
  <div
    style={{ ...style.modalOverlay, opacity: isOpen ? 1 : 0 }}
    onClick={onClose}
  >
    <div
      style={{
        ...style.modalContainer,
        transform: isOpen ? "scale(1)" : "scale(0.95)",
        opacity: isOpen ? 1 : 0,
      }}
      onClick={(e) => e.stopPropagation()}
      className="modal-enter"
    >
      <div style={style.modalHeader}>
        <h3 style={style.modalTitle}>Como Funciona o Remarketing?</h3>
        <button onClick={onClose} style={style.modalCloseButton}>
          <FiX size={20} />
        </button>
      </div>
      <div style={style.modalBody}>
        <p style={style.modalIntroText}>
          O sistema de remarketing automático usa Inteligência Artificial para
          reengajar clientes de forma estratégica. Veja como funciona:
        </p>
        <div style={style.modalStep}>
          <div style={style.stepNumber}>1</div>
          <div style={style.stepContent}>
            <h4 style={style.stepTitle}>
              <FiSearch style={style.stepIcon} /> Análise Inteligente
            </h4>
            <p style={style.stepDescription}>
              No horário e intervalo definidos, a IA analisa o histórico de
              conversas dos clientes inativos selecionados.
            </p>
          </div>
        </div>
        <div style={style.modalStep}>
          <div style={style.stepNumber}>2</div>
          <div style={style.stepContent}>
            <h4 style={style.stepTitle}>
              <FiCheckSquare style={style.stepIcon} /> Decisão Estratégica
            </h4>
            <p style={style.stepDescription}>
              Com base no contexto das conversas, a IA decide se é um momento
              oportuno para reativar o contato, buscando chances reais de
              conversão.
            </p>
          </div>
        </div>
        <div style={style.modalStep}>
          <div style={style.stepNumber}>3</div>
          <div style={style.stepContent}>
            <h4 style={style.stepTitle}>
              <FiSend style={style.stepIcon} /> Ação Automática
            </h4>
            <p style={style.stepDescription}>
              Se a oportunidade for validada, a IA envia a mensagem de
              remarketing para o cliente, reabrindo a conversa de forma natural.
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>
);

export default function Remarketing() {
  const { credentials } = useContext(AuthContext);
  const { avaliableAgents } = useContext(ChatContext);
  const { startLoading, stopLoading } = useContext(LoadingContext);
  const { showMessage } = useContext(SystemMessageContext);

  const [selectedAgent, setSelectedAgent] = useState(null);
  const [config, setConfig] = useState(null);
  const [remarketingModel, setRemarketingModel] = useState(null);
  const [isModalVisible, setIsModalVisible] = useState(false);

  const [status, setStatus] = useState(0);
  const [intervaloDias, setIntervaloDias] = useState(7);
  const [horario, setHorario] = useState("09:00");
  const [maxClients, setMaxClients] = useState(10);

  useEffect(() => {
    if (avaliableAgents && avaliableAgents.length > 0 && !selectedAgent) {
      setSelectedAgent(avaliableAgents[0]);
    }
  }, [avaliableAgents, selectedAgent]);

  const resetForms = () => {
    setStatus(0);
    setIntervaloDias(7);
    setHorario("09:00");
    setMaxClients(10);
    setConfig(null);
    setRemarketingModel(null);
  };

  const fetchAllData = async () => {
    if (!selectedAgent) return;
    startLoading();
    resetForms();
    try {
      const [configData, modelData] = await Promise.all([
        obterConfiguracaoRemarketing(
          credentials.accessToken,
          selectedAgent.number
        ),
        obterModeloRemarketing(credentials.accessToken, selectedAgent.number),
      ]);

      if (modelData) {
        setRemarketingModel(modelData);
      }

      if (configData) {
        setConfig(configData);
        setStatus(configData.status || 0);
        setIntervaloDias(configData.intervalDays || 7);
        setHorario(configData.hour ? configData.hour.substring(0, 5) : "09:00");
        setMaxClients(configData.maxClientsPerExecution || 10);
      }
    } catch (error) {
      showMessage(
        "Não foi possível carregar os dados de remarketing.",
        "error"
      );
      resetForms();
    } finally {
      stopLoading();
    }
  };

  useEffect(() => {
    fetchAllData();
  }, [selectedAgent]);

  const handleSave = async () => {
    if (!selectedAgent) {
      showMessage("Selecione um agente primeiro.", "error");
      return;
    }
    startLoading();
    try {
      const newConfig = {
        status: parseInt(status, 10),
        intervalDays: parseInt(intervaloDias, 10),
        hour: horario,
        maxClientsPerExecution: parseInt(maxClients, 10),
        message: "This message is now handled by RemarketingModel.",
      };
      const statusResponse = await salvarConfiguracaoRemarketing(
        credentials.accessToken,
        selectedAgent.number,
        newConfig
      );
      if (statusResponse === 200 || statusResponse === 204) {
        showMessage("Configuração salva com sucesso!", "success");
        setConfig(newConfig);
      } else {
        throw new Error("Falha ao salvar");
      }
    } catch (error) {
      showMessage("Erro ao salvar a configuração. Tente novamente.", "error");
    } finally {
      stopLoading();
    }
  };

  const renderContent = () => {
    if (!selectedAgent) {
      return (
        <div style={style.placeholder}>
          <p>Selecione um agente para começar a configurar.</p>
        </div>
      );
    }

    if (!remarketingModel) {
      return (
        <div style={style.placeholder}>
          <FiMessageSquare size={48} style={{ marginBottom: "20px" }} />
          <h3 style={style.placeholderTitle}>
            Modelo de Remarketing não encontrado
          </h3>
          <p style={style.placeholderText}>
            Para configurar o remarketing automático, primeiro é necessário
            criar um modelo de mensagem para este agente.
          </p>
        </div>
      );
    }

    return (
      <div style={style.cardBody}>
        <div style={style.formRow}>
          <label style={style.label}>Status</label>
          <select
            style={style.select}
            value={status}
            onChange={(e) => setStatus(e.target.value)}
          >
            <option value={1}>Ativo</option>
            <option value={0}>Inativo</option>
          </select>
        </div>

        <div style={style.formRow}>
          <label style={style.label}>Intervalo (dias)</label>
          <input
            type="number"
            style={style.input}
            value={intervaloDias}
            onChange={(e) => setIntervaloDias(e.target.value)}
            min="1"
          />
        </div>

        <div style={style.formRow}>
          <label style={style.label}>Horário de Envio</label>
          <input
            type="time"
            style={style.input}
            value={horario}
            onChange={(e) => setHorario(e.target.value)}
          />
        </div>

        <div style={style.formRow}>
          <label style={style.label}>Máximo de clientes por vez</label>
          <input
            type="number"
            style={style.input}
            value={maxClients}
            onChange={(e) => setMaxClients(e.target.value)}
            min="1"
            max="50"
          />
        </div>

        <div style={style.formRow}>
          <label style={style.label}>Seu modelo</label>
          <div style={style.messagePreviewContainer}>
            <div style={style.messageBubble}>
              {/* <div style={style.bubbleTail}></div> */}
              <div style={{ paddingRight: "10px" }}>
                <p style={style.bubbleBody}>
                  <strong style={style.bubbleBodyBold}>
                    {remarketingModel.headerText}
                  </strong>
                  {"\n" + remarketingModel.bodyText}
                  {remarketingModel.footerText &&
                    "\n\n" + remarketingModel.footerText}
                </p>
              </div>
              <div style={style.bubbleFooter}>
                <span style={style.timestamp}>20:37</span>
              </div>
            </div>
          </div>
        </div>

        <div style={style.warningBox}>
          <FiAlertTriangle style={style.warningIcon} size={24} />
          <p style={style.warningText}>
            <strong>Atenção:</strong> Cada mensagem de remarketing enviada pela
            IA utilizará <strong>1 disparo</strong> do seu saldo disponível.
            Ativar esta função pode consumir seus disparos automaticamente.
          </p>
        </div>

        <button onClick={handleSave} style={style.saveButton}>
          <FiSave style={{ marginRight: "10px" }} size={20} />
          Salvar Configurações
        </button>
      </div>
    );
  };

  return (
    <>
      {isModalVisible && (
        <InfoModal
          isOpen={isModalVisible}
          onClose={() => setIsModalVisible(false)}
        />
      )}
      <div style={style.container}>
        <div style={style.header}>
          <h1 style={style.title}>Configurar Remarketing</h1>
        </div>
        <div style={style.contentWrapper}>
          <div style={style.agentSelectorCard}>
            <label style={style.label}>Selecione o Agente</label>
            <select
              style={style.select}
              value={selectedAgent?.number || ""}
              onChange={(e) => {
                const agent = avaliableAgents.find(
                  (a) => a.number === e.target.value
                );
                setSelectedAgent(agent);
              }}
            >
              {avaliableAgents && avaliableAgents.length > 0 ? (
                avaliableAgents.map((agent) => (
                  <option key={agent.id} value={agent.number}>
                    {agent.name}
                  </option>
                ))
              ) : (
                <option>Nenhum agente disponível</option>
              )}
            </select>
          </div>

          <div style={style.configCard}>
            <div style={style.cardHeader}>
              <h2 style={style.cardTitle}>Configuração de Remarketing</h2>
              <button
                onClick={() => setIsModalVisible(true)}
                style={style.infoButton}
                className="info-button-hover"
              >
                <FiInfo style={{ marginRight: "8px" }} />
                Como funciona?
              </button>
            </div>
            {renderContent()}
          </div>
        </div>
      </div>
    </>
  );
}
